import goproTelemetry from "gopro-telemetry";
import gpmfExtract from "gpmf-extract";
import { XMLBuilder } from "fast-xml-parser";
import { downloadFile } from "../data/misc";
import { parseRawData as parseRawNmeaData } from "../data/nmeaParser";

const BlackVueH265StartPoint = 82246;
const BlackVueH264StartPoint = 82189;

export async function extractGoPro(file, onProgress, cancellationToken) {
  onProgress({
    extractionState: "Extracting Telemetry",
    extractionPercent: 0,
  });
  const progress = (percent) => onProgress({ extractionPercent: percent });
  let extracted;
  try {
    extracted = await gpmfExtract(file, {
      browserMode: true,
      progress,
      //if there is an error in extracting while using webworkers that doesn't kill the extraction process, the process can't be cancelled without refreshing the page.
      //using the standard thread rather than webWorker enables the error thrown by the extractor to hit the exception handler below
      useWorker: false,
      cancellationToken,
    });
  } catch (e) {
    if (!(e.startsWith && e.startsWith("Canceled"))) {
      throw new Error(e.message ?? e);
    }
    //extraction process was cancelled
    return;
  }

  const endDate = new Date(extracted.timing.start);
  endDate.setSeconds(endDate.getSeconds() + extracted.timing.videoDuration);

  onProgress({
    extractionState: "Processing Telemetry",
    extractionPercent: 0,
  });
  //goproTelemetry function expects node's setImmediate function to be defined, which it isn't in the browser. setTimeout 0 is equivalent
  if (!window.setImmediate) window.setImmediate = (x) => setTimeout(x, 0);
  const telemetry = await goproTelemetry(extracted, {
    //see https://github.com/JuanIrache/gopro-telemetry for options
    groupTimes: 1, //Sample at 1Hz
    disableMerging: true,
    disableInterpolaltion: true,
    stream: ["GPS5"],
    GPS5Precision: 625, //using default values from https://goprotelemetryextractor.com/free/
    GPS5Fix: 2,
    WrongSpeed: 563, //m/s
    progress: (percent) => onProgress({ extractionPercent: percent * 100 }),
  });

  const samples = telemetry["1"].streams.GPS5.samples;
  const recordedPath = samples
    .filter((x) => new Date(x.date) < endDate)
    .sort((x1, x2) => new Date(x1.date) - new Date(x2.date))
    //was sampled at 1Hz above in order to ensure all values were retrieved, now reduce to 1 record per second
    .reduce(
      (result, item) =>
        !result.length ||
        item.date.getSeconds() !== result[result.length - 1].date.getSeconds()
          ? [...result, item]
          : result,
      []
    )
    .map((x) => ({
      //see https://www.topografix.com/gpx_manual.asp#trkpt for valid fields
      "@_lat": x.value[0],
      "@_lon": x.value[1],
      time: x.date.toISOString(),
      ele: x.value[2].toFixed(2),
      speed: x.value[3].toFixed(2),
    }));

  downloadAsGpx(recordedPath, file.name, telemetry["1"]["device name"]);
}

export async function extractBlackVue(
  file,
  onProgress,
  cancellationToken,
  isHEVC = false
) {
  const startLocation = isHEVC
    ? BlackVueH265StartPoint
    : BlackVueH264StartPoint;

  //extracts the NMEA lines from the video file, given startLocation
  function getNMEA(progress) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      const decoder = new TextDecoder();
      const fileSize = file.size;
      //not accurate, but from testing should give some semblence of progress
      const likelyChunkCount = fileSize / 4925000;
      let location = startLocation;
      let i = 0;
      const results = [];
      reader.onload = () => {
        progress((++i / likelyChunkCount) * 100);
        const textResult = decoder.decode(reader.result);
        results.push(textResult);
        if (location > fileSize || new Uint8Array(reader.result).includes(0))
          resolve(results.join("").replaceAll("\n\n", "\n"));
        else {
          if (cancellationToken && cancellationToken.cancelled) resolve(null);
          else {
            location += 1000;
            reader.readAsArrayBuffer(file.slice(location, location + 1000));
          }
        }
      };
      reader.readAsArrayBuffer(file.slice(location, location + 1000));
    });
  }

  onProgress({
    extractionState: "Extracting GPS Data",
    extractionPercent: 0,
  });

  const nmea = await getNMEA((extractionPercent) =>
    onProgress({ extractionPercent })
  );
  if (!nmea)
    //cancelled
    return;
  const recordedPath = parseRawNmeaData(nmea).map((x) => {
    const speed = parseInt(x.speed) / 3.6;
    return {
      //see https://www.topografix.com/gpx_manual.asp#trkpt for valid fields
      "@_lat": x.latitude,
      "@_lon": x.longitude,
      time: x.dateTime.toISOString(),
      speed: isNaN(speed) ? undefined : speed,
    };
  });

  downloadAsGpx(
    recordedPath,
    file.name,
    isHEVC ? "BlackVue HEVC" : "BlackVue AVC"
  );
}

function downloadAsGpx(recordedPath, fileName, name) {
  const xmlAsJson = {
    gpx: {
      "@_xmlns": "http://www.topografix.com/GPX/1/0",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      trk: {
        name,
        trkseg: {
          trkpt: recordedPath,
        },
      },
    },
  };
  const xmlBuilder = new XMLBuilder({
    ignoreAttributes: false,
  });
  const xml = xmlBuilder.build(xmlAsJson);

  //download file
  const gpx = new Blob([xml], {
    type: "application/gpx+xml",
    endings: "native",
  });
  downloadFile(
    URL.createObjectURL(gpx),
    `${fileName.substr(0, fileName.lastIndexOf("."))}.GPX`
  );
}
